import { Authorisation } from "@iventis/domain-model/model/authorisation";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { Node } from "@iventis/domain-model/model/node";
import { Permission } from "@iventis/domain-model/model/permission";
import { PermissionOwnerType } from "@iventis/domain-model/model/permissionOwnerType";
import { PermissionResourceType } from "@iventis/domain-model/model/permissionResourceType";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { isAdmin } from "@iventis/utilities";
import { libraryApi } from "@iventis/api/src/api";
import { translate } from "@iventis/translations/translation";
import { Content } from "@iventis/translations";
import { GroupOption, UserOption } from "./user-search.types";

export const getPermissions = async (
    node: Node, // The node to get permissions for
    isNodeCreated = true // If the node might not yet be created, we can use still use the parent Id to get the inherited permissions.
) => {
    const { data: permissions } = await libraryApi.get<Permission[]>(getPermssionUrl(node.type, isNodeCreated ? node.id : node.parentId));
    return permissions;
};

// Does the user have authorisation to lock/unlock the resource
export const canLock = async (
    resourceId: string // Resource Id
) => {
    const { data: authorisation } = await libraryApi.get<Authorisation>(`${libraryPermissionsResourceUrl}${resourceId}/user?excludeLocks=true`);
    return isAdmin(authorisation);
};

export const breakInheritance = async (resourceId: string) => {
    const { data: permissions } = await libraryApi.post<Permission[]>(`/break-inheritance/${resourceId}`, {
        customErrorMessage: {
            title: translate(Content.errors.custom_network_error.generic.title, { action: translate(Content.permissions.turnOffInheritance) }),
            message: translate(Content.errors.custom_network_error.generic.message, { action: translate(Content.permissions.turnOffInheritance) }),
        },
    });
    return permissions;
};

export const restoreInheritance = async (resourceId: string) => {
    const { data: permissions } = await libraryApi.delete<Permission[]>(`/break-inheritance/${resourceId}`, {
        customErrorMessage: {
            title: translate(Content.errors.custom_network_error.generic.title, { action: translate(Content.permissions.turnOnInheritance) }),
            message: translate(Content.errors.custom_network_error.generic.message, { action: translate(Content.permissions.turnOnInheritance) }),
        },
    });
    return permissions;
};

function getPermssionUrl(type: NodeType, resourceId: string) {
    const libraryPermissionsUrl = libraryPermissionsResourceUrl + resourceId;

    const mapTypesToUrl: { [type in NodeType]?: string } = {
        [NodeType.Folder]: libraryPermissionsUrl,
        [NodeType.Layer]: libraryPermissionsUrl,
        [NodeType.Map]: libraryPermissionsUrl,
        [NodeType.ScheduleVersion]: libraryPermissionsUrl,
    };

    const url = mapTypesToUrl[type];

    if (url === undefined) {
        throw new Error(`Permission url is not defined for node type "${type}"`);
    }

    return url;
}

export function getLabel(item: GroupOption | UserOption) {
    if (item.type === PermissionOwnerType.User) {
        return `${item.value.firstName} ${item.value.lastName}`;
    }
    if (item.type === PermissionOwnerType.Group) {
        return item.value.name;
    }
    throw new Error("Option type was not recognised");
}

export function diffPermissions(oldPermissions: Permission[], newPermissions: Permission[]): { create: Permission[]; update: Permission[]; delete: Permission[] } {
    // Filter the new permissions so that we have the ones that do not exist in the old permissions
    const create = newPermissions.filter(({ id: newPermissionId }) => !oldPermissions.some(({ id: oldPermissionId }) => oldPermissionId === newPermissionId));

    // Filter the new permissions so that we have the ones which have IDs that exist in the old permissions, but do not have the same authorisation level
    const update = newPermissions.filter(({ id: newPermissionId, authorisation: newPermissionAuth }) =>
        oldPermissions.some(({ id: oldPermissionId, authorisation: oldPermissionAuth }) => newPermissionId === oldPermissionId && oldPermissionAuth !== newPermissionAuth)
    );

    // Filter the old permissions such that we have only the permissions which no longer exist in new permissions
    const toDelete = oldPermissions.filter(({ id: oldPermissionId }) => !newPermissions.some(({ id: newPermissionId }) => newPermissionId === oldPermissionId));

    return {
        create,
        update,
        delete: toDelete,
    };
}

export const hasPermissionsChanged = (originalPermissions: Permission[], latestPermissions: Permission[]) =>
    originalPermissions && latestPermissions && Object.values(diffPermissions(originalPermissions, latestPermissions)).some((modifications) => modifications.length > 0);

export const libraryPermissionBaseURL = `/hierarchy_permissions/`;

const libraryPermissionsResourceUrl = `/hierarchy_permissions/resource/`;

export const libraryPermissionLockUrl = "/lock/";

export const getPermissionResourceIconName = (resourceType: PermissionResourceType): IconProp => {
    switch (resourceType) {
        case PermissionResourceType.Folder:
            return ["fas", "folder"];
        case PermissionResourceType.Map:
            return ["fas", "map"];
        case PermissionResourceType.Layer:
            return ["fas", "layer-group"];
        case PermissionResourceType.Schedule:
            return ["fas", "calendar"];
        case PermissionResourceType.ScheduleVersion:
            return ["fas", "calendar"];
        default:
            return ["fas", "file"];
    }
};
